import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Sparkles, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

const STORAGE_PREFIX = "ff-tour-done:";

/**
 * Role-aware first-login walkthrough. Renders a soft glass modal with
 * step-by-step slides. Each user sees it once (localStorage).
 */
const TOURS = {
  PRODUCT_OWNER: [
    { title: "Welcome to FulfillFlow, Owner.", body: "You're the platform super-admin. You can see every warehouse, every user, and every action across the entire installation." },
    { title: "Spin up a Warehouse.", body: "Head to Warehouses to create your first tenant. Each warehouse is fully isolated — its own data, its own team.", cta: "Go to Warehouses", to: "/warehouses" },
    { title: "Invite a 3PL Owner.", body: "Once a warehouse exists, invite a 3PL Owner to run it. They'll be the tenant admin with full access inside their warehouse only.", cta: "Go to Users", to: "/users" },
    { title: "Command Palette · Cmd+K.", body: "Press ⌘K (or Ctrl+K) anywhere to jump to any page, invite users, create orders — everything, one keystroke away." },
    { title: "You're set.", body: "The Danger Zone in Settings gives you a one-click wipe if you ever need to reset the environment. Have fun." },
  ],
  THREE_PL_OWNER: [
    { title: "Welcome to your Warehouse.", body: "You're the 3PL Owner — full access inside your warehouse. Add clients, connect stores, invite your team." },
    { title: "Add your first Client.", body: "Clients are the brands you fulfill for. Each one owns its own stores and inventory.", cta: "Go to Clients", to: "/clients" },
    { title: "Connect a Store.", body: "Once you have a client, wire up their Shopify, Amazon, eBay or Walmart store. Orders will start flowing in.", cta: "Go to Stores", to: "/stores" },
    { title: "Invite your Team.", body: "Bring in Warehouse Managers, Staff, or VA Agency partners. Each role sees only what they need.", cta: "Go to Users", to: "/users" },
    { title: "You're ready to ship.", body: "The Dashboard shows today's work at a glance. Press ⌘K anywhere for lightning-fast navigation." },
  ],
  WAREHOUSE_MANAGER: [
    { title: "You run the floor.", body: "You have full read/write on orders, inbound, inventory, returns and invoicing for your warehouse." },
    { title: "Start on the Dashboard.", body: "The Dashboard surfaces low stock, pending orders and returns waiting on you." },
    { title: "Fulfill Orders.", body: "Head to Orders to pick, pack and ship. Status changes are audited on every move.", cta: "Go to Orders", to: "/orders" },
    { title: "Press ⌘K anywhere", body: "Global search takes you to any order, client, or setting in one keystroke." },
  ],
  WAREHOUSE_STAFF: [
    { title: "Welcome aboard.", body: "You handle orders, inbound receiving, inventory counts and returns. Everything you see is scoped to your warehouse." },
    { title: "Your queue is in Orders.", body: "Filter to 'Pending' and 'Ready' to see what needs picking today.", cta: "Go to Orders", to: "/orders" },
    { title: "Receiving? Head to Inbound.", body: "Log incoming shipments and they'll flow straight into inventory.", cta: "Go to Inbound", to: "/inbound" },
  ],
  VA_AGENCY_OWNER: [
    { title: "Welcome, VA Agency Owner.", body: "You see the stores assigned to your agency and their orders — nothing else. Your team's scope is inherited from yours." },
    { title: "Your stores live here.", body: "Only stores explicitly assigned to your agency show up.", cta: "Go to Stores", to: "/stores" },
    { title: "Manage your team.", body: "Invite VA Managers and VA Staff. They'll see the same scope you do.", cta: "Go to Users", to: "/users" },
  ],
  VA_MANAGER: [
    { title: "Welcome, VA Manager.", body: "You have the same access as your agency owner — stores assigned to your agency and their orders." },
    { title: "Start with Orders.", body: "Filter by store to work through a specific brand's queue.", cta: "Go to Orders", to: "/orders" },
  ],
  VA_STAFF: [
    { title: "Welcome, VA Staff.", body: "You're assigned to specific stores. Focus on the orders that need your attention today." },
    { title: "Your orders queue.", body: "Head to Orders — you'll only see the stores you're assigned to.", cta: "Go to Orders", to: "/orders" },
  ],
  CLIENT: [
    { title: "Welcome to your storefront view.", body: "You're a client. You see your own store, inventory, orders and returns — read-only, updated live by the warehouse team." },
    { title: "Track your orders.", body: "Orders shows the current status of every shipment we're handling for you.", cta: "Go to Orders", to: "/orders" },
    { title: "Check stock.", body: "Inventory reflects live counts as your warehouse team receives and ships.", cta: "Go to Inventory", to: "/inventory" },
  ],
};

export function GuidedTour() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  const key = user ? `${STORAGE_PREFIX}${user.id}` : null;
  const steps = useMemo(() => TOURS[user?.role] || null, [user?.role]);

  useEffect(() => {
    if (loading || !user || !key || !steps) return;
    try {
      if (window.localStorage.getItem(key)) return;
      // Give the shell a beat to mount before showing the tour.
      const t = setTimeout(() => setOpen(true), 700);
      return () => clearTimeout(t);
    } catch (e) {
      // ignore
    }
  }, [loading, user, key, steps]);

  const finish = () => {
    try { if (key) window.localStorage.setItem(key, "1"); } catch (e) { /* ignore */ }
    setOpen(false);
    setStep(0);
  };
  const next = () => {
    if (!steps) return;
    if (step >= steps.length - 1) finish();
    else setStep((s) => s + 1);
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  if (!steps) return null;

  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="tour-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] bg-background/50 backdrop-blur-sm"
            data-testid="guided-tour-backdrop"
          />
          <motion.div
            key="tour-modal"
            role="dialog"
            aria-labelledby="tour-title"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="glass-strong shadow-premium fixed left-1/2 top-1/2 z-[81] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl p-8"
            data-testid="guided-tour"
          >
            <button
              type="button"
              onClick={finish}
              aria-label="Skip tour"
              data-testid="guided-tour-close"
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-ring"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="mb-5 flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/12 text-primary ring-1 ring-inset ring-primary/20">
                {step === 0 ? <Sparkles className="h-4 w-4" /> : isLast ? <Rocket className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
              </span>
              <span className="overline">Step {step + 1} of {steps.length}</span>
            </div>

            <motion.div
              key={step}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.25 }}
            >
              <h2 id="tour-title" className="font-display text-2xl font-semibold tracking-tight text-foreground">
                {current.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {current.body}
              </p>
            </motion.div>

            {/* Progress bar */}
            <div className="mt-6 flex gap-1.5">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-muted"}`}
                />
              ))}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <div>
                {step > 0 && (
                  <Button variant="ghost" size="sm" onClick={prev} className="gap-1" data-testid="guided-tour-back">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={finish} data-testid="guided-tour-skip">
                  Skip
                </Button>
                {current.cta && current.to ? (
                  <Button
                    size="sm"
                    onClick={() => { finish(); navigate(current.to); }}
                    className="gap-1"
                    data-testid="guided-tour-cta"
                  >
                    {current.cta}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                ) : (
                  <Button size="sm" onClick={next} className="gap-1" data-testid="guided-tour-next">
                    {isLast ? "Get started" : "Next"}
                    {!isLast && <ArrowRight className="h-3.5 w-3.5" />}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
